import type { Livre } from "./moteur/types";
import type { Changements, Partie } from "./partie";

/**
 * Le carnet : ce que le telephone retient a la place de la feuille d'aventure. Tous les indices
 * du livre y sont, comme sur la feuille imprimee, ceux qu'on a trouves coches.
 */
export function Carnet({ livre, partie }: { livre: Livre; partie: Partie }) {
  if (livre.indices.length === 0 && livre.jauges.length === 0) return null;
  return (
    <section
      aria-labelledby="titre-carnet"
      className="rounded-lg border border-border bg-card p-4 text-card-foreground"
    >
      <h2 id="titre-carnet" className="font-serif text-lg font-semibold">
        Carnet
      </h2>
      {livre.indices.length > 0 ? (
        <ul aria-label="Indices" className="mt-3 flex flex-col gap-1.5">
          {livre.indices.map((indice) => {
            const trouve = partie.etat.indices.includes(indice.id);
            return (
              <li key={indice.id} className={trouve ? "" : "text-muted-foreground"}>
                <span aria-hidden="true" className="inline-block w-6 font-semibold">
                  {trouve ? "✓" : "○"}
                </span>
                {indice.libelle}
                <span className="sr-only">{trouve ? " : trouvé" : " : pas encore trouvé"}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
      {livre.jauges.map((jauge) => {
        const valeur = partie.etat.jauges[jauge.id] ?? 0;
        return (
          <div key={jauge.id} className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-medium">{jauge.libelle}</span>
            <span aria-hidden="true" className="flex gap-1">
              {Array.from({ length: jauge.max }, (_, rang) => rang + 1).map((numero) => (
                <span
                  key={numero}
                  className={`size-4 rounded-sm border border-foreground/40 ${numero <= valeur ? "bg-primary" : ""}`}
                />
              ))}
            </span>
            <span className="text-sm text-muted-foreground">
              {valeur} sur {jauge.max}
            </span>
          </div>
        );
      })}
    </section>
  );
}

/** Ce que le dernier choix a change dans le carnet, montre juste sous la page a lire. */
export function Nouveautes({ changements }: { changements: Changements }) {
  const lignes = [
    ...changements.gagnes.map((libelle) => `Nouvel indice : ${libelle}`),
    ...changements.perdus.map((libelle) => `Indice perdu : ${libelle}`),
    ...changements.jauges.map(
      (jauge) => `${jauge.libelle} : ${jauge.avant} → ${jauge.apres} sur ${jauge.max}`,
    ),
  ];
  if (lignes.length === 0) return null;
  return (
    <ul
      aria-label="Dans votre carnet"
      className="flex flex-col gap-1 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm"
    >
      {lignes.map((ligne) => (
        <li key={ligne}>{ligne}</li>
      ))}
    </ul>
  );
}
